/**
 * Token delta % util for admin dashboard (US-04-07)
 * Shows how far off estimate is from real — helps tune output_ratio formula.
 */

/** Format token count as abbreviated "12.4K" style */
export function formatK(n: number | null | undefined): string {
  if (n == null || n === 0) return '—';
  if (n < 1000) return String(n);
  return (n / 1000).toFixed(1) + 'K';
}

// === Exchange rate + revenue constants ===
// Gói Vừa (tier phổ biến): 199,000đ / 1,500,000 credits = 0.1327 VND/credit
export const VND_PER_CREDIT_STANDARD = 199_000 / 1_500_000;
// USD ↔ VND conversion rate (cố định, update khi cần)
export const USD_TO_VND = 25_000;
// Revenue per credit in USD (derived): 0.1327 / 25000 ≈ $0.0000053
export const USD_PER_CREDIT_STANDARD = VND_PER_CREDIT_STANDARD / USD_TO_VND;

export type Currency = 'USD' | 'VND';

/**
 * Format money in specified currency.
 * Input is ALWAYS USD (canonical). Converts to VND on display if needed.
 */
export function formatMoney(usd: number | null | undefined, currency: Currency = 'USD'): string {
  if (usd == null || usd === 0) return '—';
  if (currency === 'VND') {
    const vnd = usd * USD_TO_VND;
    if (vnd < 1) return '<1đ';
    if (vnd < 1000) return Math.round(vnd) + 'đ';
    if (vnd < 1_000_000) return (vnd / 1000).toFixed(1) + 'Kđ';
    return (vnd / 1_000_000).toFixed(2) + 'Mđ';
  }
  // USD
  if (Math.abs(usd) < 0.0001) return '<$0.0001';
  if (Math.abs(usd) < 1) return '$' + usd.toFixed(4);
  return '$' + usd.toFixed(2);
}

/** Legacy alias for Gemini cost display (always USD from DB) */
export function formatCost(usd: number | null | undefined): string {
  return formatMoney(usd, 'USD');
}

/** @deprecated Use formatMoney(usd, 'VND') instead */
export function formatVnd(vnd: number | null | undefined): string {
  if (vnd == null || vnd === 0) return '—';
  if (vnd < 1000) return Math.round(vnd) + 'đ';
  if (vnd < 1_000_000) return (vnd / 1000).toFixed(1) + 'Kđ';
  return (vnd / 1_000_000).toFixed(2) + 'Mđ';
}

export interface MoneyResult {
  revenueUsd: number;      // Ước tính thu từ user (Gói Vừa tier)
  costUsd: number;         // Chi phí Gemini thật (từ DB cost_usd)
  profitUsd: number;       // Lãi = revenue − cost
  marginPct: number;       // % margin = profit / revenue × 100
  marginColorClass: string;
}

/**
 * Calculate revenue + cost + profit per job (ALL in USD canonical).
 * Display layer converts USD → VND nếu cần qua formatMoney(val, 'VND').
 */
export function calculateMoney(tokensUsed: number | null | undefined, costUsd: number | null | undefined): MoneyResult {
  const tokens = tokensUsed || 0;
  const cost = costUsd || 0;
  const revenueUsd = tokens * USD_PER_CREDIT_STANDARD;
  const profitUsd = revenueUsd - cost;
  const marginPct = revenueUsd > 0 ? (profitUsd / revenueUsd) * 100 : 0;

  let marginColorClass: string;
  if (revenueUsd === 0) marginColorClass = 'text-slate-400';
  else if (marginPct > 70) marginColorClass = 'text-emerald-600';
  else if (marginPct > 40) marginColorClass = 'text-green-600';
  else if (marginPct > 0) marginColorClass = 'text-yellow-600';
  else marginColorClass = 'text-red-600';

  return { revenueUsd, costUsd: cost, profitUsd, marginPct, marginColorClass };
}

export interface DeltaResult {
  percent: number;        // signed: positive = real > estimate, negative = real < estimate
  absPercent: number;     // absolute value
  colorClass: string;     // bg tailwind class
  label: string;          // "+23%" | "−8%" | "—"
}

/**
 * Calculate delta % between estimate vs real tokens.
 * Returns NULL if estimate is 0 or missing (e.g. QC/audit without pre-flight).
 */
export function calculateDelta(estimate: number | null | undefined, real: number | null | undefined): DeltaResult {
  if (!estimate || estimate === 0 || real == null || real === 0) {
    return { percent: 0, absPercent: 0, colorClass: 'bg-slate-50 text-slate-400', label: '—' };
  }
  const percent = ((real - estimate) / estimate) * 100;
  const absPercent = Math.abs(percent);

  let colorClass: string;
  if (absPercent < 10) colorClass = 'bg-green-50 text-green-700';
  else if (absPercent < 20) colorClass = 'bg-yellow-50 text-yellow-700';
  else colorClass = 'bg-red-50 text-red-700';

  const sign = percent >= 0 ? '+' : '−';
  const label = `${sign}${absPercent.toFixed(0)}%`;

  return { percent, absPercent, colorClass, label };
}
